import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, FileDown, Plus, Search, Filter, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { ESTADOS_CICLO, TIPOS_FIDEICOMISO, labelTipo } from "../lib/catalogs";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/roles";

export default function FideicomisosList() {
    const { hasRole } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ tipo: "__all", estado: "__all", q: "" });

    const canCreate = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.JURIDICO, ROLES.ADMIN_TI]);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.tipo !== "__all") params.tipo = filters.tipo;
            if (filters.estado !== "__all") params.estado = filters.estado;
            if (filters.q) params.q = filters.q;
            const { data } = await api.get("/fideicomisos", { params });
            setRows(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const exportar = async (fmt) => {
        try {
            const res = await api.get(`/fideicomisos/export.${fmt}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `fideicomisos.${fmt}`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 800);
        } catch (err) {
            toast.error("No se pudo exportar", { description: err?.response?.data?.detail });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1600px]" data-testid="fideicomisos-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Building2 className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Fideicomisos
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Expediente maestro por fideicomiso. Filtra, exporta y abre la ficha.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => exportar("xlsx")} data-testid="export-xlsx">
                        <FileDown className="h-4 w-4 mr-2" /> Excel
                    </Button>
                    <Button variant="outline" onClick={() => exportar("pdf")} data-testid="export-pdf">
                        <FileDown className="h-4 w-4 mr-2" /> PDF
                    </Button>
                    {canCreate && (
                        <Button onClick={() => navigate("/fideicomisos/nuevo")} data-testid="new-fideicomiso">
                            <Plus className="h-4 w-4 mr-2" /> Nuevo fideicomiso
                        </Button>
                    )}
                </div>
            </div>

            <div className="kpi-card">
                <div className="flex items-center gap-3 mb-4">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filtros</span>
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Buscar
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input
                                className="pl-9 mt-1"
                                placeholder="TRF-…, F/…, propósito"
                                value={filters.q}
                                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                data-testid="filter-q"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Tipo
                        </label>
                        <Select value={filters.tipo} onValueChange={(v) => setFilters({ ...filters, tipo: v })}>
                            <SelectTrigger className="mt-1" data-testid="filter-tipo">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all">Todos</SelectItem>
                                {TIPOS_FIDEICOMISO.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Estado
                        </label>
                        <Select
                            value={filters.estado}
                            onValueChange={(v) => setFilters({ ...filters, estado: v })}
                        >
                            <SelectTrigger className="mt-1" data-testid="filter-estado">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all">Todos</SelectItem>
                                {ESTADOS_CICLO.map((e) => (
                                    <SelectItem key={e.value} value={e.value}>
                                        {e.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-end">
                        <Button onClick={load} className="w-full" disabled={loading}>
                            {loading ? "Cargando…" : "Aplicar filtros"}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="fideicomisos-table">
                    <thead>
                        <tr>
                            <th>Interno</th>
                            <th>Fiduciario</th>
                            <th>Tipo</th>
                            <th>Propósito</th>
                            <th>Constitución</th>
                            <th>Vigencia</th>
                            <th>Estado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((f) => {
                            const est = ESTADOS_CICLO.find((e) => e.value === f.estado_ciclo);
                            const badgeClass =
                                est?.tone === "success" ? "badge-exito" : "badge-gold";
                            return (
                                <tr
                                    key={f.id}
                                    className="cursor-pointer"
                                    onClick={() => navigate(`/fideicomisos/${f.id}`)}
                                    data-testid={`fideicomiso-row-${f.numero_interno}`}
                                >
                                    <td className="font-medium">{f.numero_interno}</td>
                                    <td className="tabular">{f.numero_fiduciario}</td>
                                    <td>
                                        <span className="badge-gold">
                                            {labelTipo(f.tipo, TIPOS_FIDEICOMISO)}
                                        </span>
                                    </td>
                                    <td className="text-sm">{f.proposito}</td>
                                    <td className="tabular text-xs">{f.fecha_constitucion}</td>
                                    <td className="tabular text-xs">{f.vigencia || "—"}</td>
                                    <td>
                                        <span className={badgeClass}>
                                            {est?.label || f.estado_ciclo}
                                        </span>
                                    </td>
                                    <td>
                                        <Link
                                            to={`/fideicomisos/${f.id}`}
                                            className="text-xs text-[color:var(--brand-dark-gold)] flex items-center gap-1 hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Abrir <ArrowRight className="h-3 w-3" />
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={8} className="text-center py-10 text-muted-foreground">
                                    Sin resultados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
